// web/src/tutorial/quickstart/fret-dance.ts
// Fret Dance · Quick Start（最短流程：选演奏者 → 选 MIDI → 生成动画）
// 本页只走最短路径，参数细节一律下沉到 /details/fret-dance.html。
//
// 依据：G:\fretDance_rust
//   src/ui/app.rs（四个页签与顺序）、parameter_setting.rs、avatar_display.rs、avatar.rs、
//   midi_info_scan.rs、execute_operation.rs、execute_logic.rs、show_console.rs、
//   hand_recorder_editor.rs、fret_timeline.rs、data.rs（默认值）、
//   src/fret_dancer.rs（输出路径与报告文件）、
//   asset/i18n/zh-CN.json 与 asset/i18n/en-US.json（界面原文，逐字对齐）

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const fretDanceQuickstart: TutorialDoc = {
  key: "fret_dance",
  slug: "fret-dance",
  kind: "quickstart",
  icon: "🎸",
  title: l("Fret Dance 吉他演奏动画", "Fret Dance Guitar Performance"),
  subtitle: l(
    "三步跑通：选演奏者 → 选 MIDI → 生成动画。本文只走最短路径，所有参数保持默认值；想调参数、想看每个滑块管什么，去《软件界面详细说明》。",
    "Three steps to a result: pick a performer, pick a MIDI file, generate. This guide stays on the shortest path and leaves every parameter at its default — for tuning and for what each control does, see the UI Reference.",
  ),
  readMinutes: 6,
  video: {
    url: "https://www.bilibili.com/video/BV1sZ9qB2EMD/",
    youtube: "https://www.youtube.com/watch?v=-HCCcGp-ktw",
    caption: l(
      "Fret Dance 完整操作演示：虚幻引擎中的吉他手 第5集 生成动画以及debug（B 站）",
      "Fret Dance full walkthrough: Part 5 — Generate animation and debug (YouTube)",
    ),
    series: [
      { title: l("Midi转演奏动画工具 - FretDance桌面版开放下载", "MIDI-to-performance tool — FretDance desktop released"), url: "https://www.bilibili.com/video/BV1ASH7zbEr5/" },
      { title: l("桌面版的使用讲解", "Using the desktop app"), url: "https://www.bilibili.com/video/BV1ASH7zbEtD/" },
      { title: l("FretDance使用介绍 blender & python & 吉它 & animation", "Introducing FretDance: Blender & Python & guitar & animation"), url: "https://www.bilibili.com/video/BV1UT421i71j/" },
      { title: l("FretDance新增基于音乐的吉他偏移动画", "FretDance adds music-driven guitar offset animation"), url: "https://www.bilibili.com/video/BV1c7Lc6yEgS/" },
      { title: l("虚幻引擎中的吉他手 - FretDanceUnreal系列教程", "FretDance Unreal series, Part 0 — Introduction"), url: "https://www.bilibili.com/video/BV1NZodB3Ewf/", youtube: "https://www.youtube.com/watch?v=hPvs62F2rU0" },
    ],
  },
  images: [
    {
      src: "/assets/image/products/fret-dance/CN/角色设置.png",
      srcEn: "/assets/image/products/fret-dance/EN/Character Parameters.png",
      label: l("角色设置", "Character Settings"),
      width: 1102,
      height: 932,
    },
    {
      src: "/assets/image/products/fret-dance/CN/MIDI选择.png",
      srcEn: "/assets/image/products/fret-dance/EN/MIDI Select.png",
      label: l("MIDI选择", "MIDI Select"),
      width: 1102,
      height: 932,
    },
    {
      src: "/assets/image/products/fret-dance/CN/指法时间轴.png",
      srcEn: "/assets/image/products/fret-dance/EN/Fingering timeline.png",
      label: l("指法时间轴", "Fingering Timeline"),
      width: 1102,
      height: 932,
    },
    {
      src: "/assets/image/products/fret-dance/CN/生成动画.png",
      srcEn: "/assets/image/products/fret-dance/EN/Generate Animation.png",
      label: l("生成动画", "Generate"),
      width: 1102,
      height: 932,
    },
    {
      src: "/assets/image/products/fret-dance/CN/右手Recorder编辑器.png",
      srcEn: "/assets/image/products/fret-dance/EN/Right-Hand Recorder Editor.png",
      label: l("右手编辑器", "Recorder Editor"),
      width: 1102,
      height: 932,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "overview",
      title: l("这一篇要做什么", "What this page does"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "Fret Dance 把一份 MIDI 变成**吉他（或贝斯、电吉他）的演奏动画**。你只要告诉它「谁来弹」和「弹哪首曲子」，它就会自己算出左手按哪根弦的第几品、右手用哪根手指拨弦，写出左右手逐帧动画数据、琴弦动画数据和一份报告 JSON。之后在 Blender 或 Unreal 的 MusicDoll 插件里加载那份报告，就能播放或渲染。**整个过程中你一帧都不用手摆。**",
            "Fret Dance turns a MIDI file into **guitar (or bass, or electric guitar) performance animation**. You only tell it who plays and which song; it works out which fret each left-hand finger presses, which right-hand finger plucks, and writes per-frame left-hand and right-hand animation data, guitar string animation data and a report JSON. Load that report in the MusicDoll add-on for Blender or Unreal and you can play or render it. **You never hand-key a frame.**",
          ),
        },
        {
          kind: "steps",
          items: [
            {
              title: l("选演奏者", "Pick a performer"),
              text: l(
                "在 `角色设置` 页的 `Avatar:` 下拉里选一个角色。软件自带一批（指弹吉他、贝斯、电吉他都有），选完看一眼右栏 `乐器:` 是不是你想要的。",
                "Choose a character from the `Avatar:` dropdown on the `角色设置` / Character Settings tab. A set ships with the app — fingerstyle guitar, bass and electric guitar. Check the `乐器:` / Instrument line on the right afterwards.",
              ),
            },
            {
              title: l("选 MIDI", "Pick a MIDI file"),
              text: l(
                "在 `MIDI选择` 页选好 `.mid` / `.midi` 文件，点 `🔍 扫描MIDI信息`，勾选弹琴的那几轨，再点 `✅ 添加选中的轨道`。",
                "On the `MIDI选择` / MIDI Select tab, open a `.mid` / `.midi` file, click `🔍 扫描MIDI信息` / Scan MIDI Info, tick the tracks that carry the guitar part and click `✅ 添加选中的轨道` / Add selected tracks.",
              ),
            },
            {
              title: l("生成动画", "Generate"),
              text: l(
                "在 `生成动画` 页点 `一键生成所有数据`，等右侧控制台跑完，用 `复制结果` 拿到报告文件路径。",
                "On the `生成动画` / Generate tab press `一键生成所有数据` / Generate all data in one go, let the console finish, then use `复制结果` / Copy Result to grab the report path.",
              ),
            },
          ],
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "这一页只讲**怎么跑通**。每一个滑块的范围、默认值，以及「调了会变成什么样」，都写在[软件界面详细说明](/details/fret-dance.html)里——第一次跑通不需要打开它。",
            "This page only covers **getting a result**. The range, default and effect of every slider live in the [UI Reference](/details/fret-dance.html) — you do not need it for your first run.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "details-link",
      title: l("什么时候该去翻详细说明", "When to open the UI Reference"),
      blocks: [
        {
          kind: "p",
          text: l(
            "本篇刻意**不碰参数**：换角色、换曲子、点生成，三步就结束。等你在插件里看过一遍效果，觉得某个地方不像真人弹琴（手落得太慢、揉弦太夸张、该响的弦没响），再回[软件界面详细说明](/details/fret-dance.html)：它按 `角色设置` → `MIDI选择` → `生成动画` → `右手编辑器` 四个页签逐屏拆开，每个可调参数都有一行——管什么、范围多少、默认值、什么时候该动它。",
            "This page deliberately **skips the parameters**: switch performer, switch song, press generate, done. Once you have watched the result in your 3D app and something looks off — hands landing too slowly, vibrato too wide, a string that should ring staying silent — come back to the [UI Reference](/details/fret-dance.html): it walks the four tabs — `角色设置` / Character Settings, `MIDI选择` / MIDI Select, `生成动画` / Generate and `右手编辑器` / Recorder Editor — screen by screen, with a line for every adjustable parameter: what it does, its range, its default and when to touch it.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "prepare",
      title: l("开始之前：准备什么", "Before you start"),
      blocks: [
        {
          kind: "ul",
          items: [
            l(
              "**一份 MIDI 文件**（`.mid` 或 `.midi`）。里面可以带鼓、贝斯、人声等别的轨道，不影响——后面勾选轨道时只挑弹琴的那几轨就行。",
              "**One MIDI file** (`.mid` or `.midi`). Extra drum, bass or vocal tracks are fine — you simply tick only the guitar tracks when selecting.",
            ),
            l(
              "**演奏者不用自己准备**。软件自带一批角色，启动后在 `角色设置` 页的下拉菜单里直接选；要把自己的模型做成可用角色，见[自定义弹拨演奏者](/advanced/fret-dance.html)。",
              "**No performer to prepare.** A set of characters ships with the app — just pick one from the dropdown on the `角色设置` / Character Settings tab. To turn your own model into a performer, see [Custom Plucked-String Performer](/advanced/fret-dance.html).",
            ),
          ],
        },
        {
          kind: "note",
          text: l(
            "程序启动时 `MIDI文件管理` 卡片里已经预填了一个示例文件名（`asset/midi/Sunburst.mid`）。如果你的安装目录里没有这个文件，点扫描会直接报错——点 `📂 选择MIDI文件` 换成自己的文件即可。",
            "On startup the `MIDI文件管理` card is pre-filled with a sample name (`asset/midi/Sunburst.mid`). If that file is not in your install folder, scanning fails immediately — just press `📂 选择MIDI文件` / Select MIDI File and pick your own.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "ui-overview",
      title: l("界面总览：四个页签，按顺序点", "The four tabs, in order"),
      image: { image: 1 },
      blocks: [
        {
          kind: "p",
          text: l(
            "窗口顶部一排是四个页签。前三个就是本文的流程顺序，第四个是可选工具：",
            "Four tabs sit across the top of the window. The first three are this guide's flow; the fourth is an optional tool:",
          ),
        },
        {
          kind: "table",
          head: [l("页签", "Tab"), l("干什么", "What it does"), l("什么时候用", "When you use it")],
          rows: [
            [
              l("`角色设置`", "`角色设置` / Character Settings"),
              l("选演奏者，确认乐器类型，调弦与手部动作节奏", "Pick the performer, confirm the instrument, set tuning and hand timing"),
              l("每次生成前扫一眼；换角色必看", "A glance before every run; required when switching performers"),
            ],
            [
              l("`MIDI选择`", "`MIDI选择` / MIDI Select"),
              l("选 MIDI 文件、扫描轨道、勾选要生成动画的轨道", "Open the MIDI, scan its tracks, tick the ones to animate"),
              l("换曲子时", "Whenever you switch songs"),
            ],
            [
              l("`生成动画`", "`生成动画` / Generate"),
              l("一键生成全部数据，右侧控制台显示进度", "Generate everything in one go, with progress in the console on the right"),
              l("跑流程的第 3 步", "Step 3 of the flow"),
            ],
            [
              l("`右手编辑器`", "`右手编辑器` / Recorder Editor"),
              l("看左手按弦时间轴、手工修电吉他右手摇把记录", "Inspect the left-hand fingering timeline and hand-edit electric-guitar whammy records"),
              l("只在需要检查或微调时打开", "Only when inspecting or fine-tuning"),
            ],
          ],
        },
        {
          kind: "note",
          text: l(
            "右上角还有 `语言`、`帮助`、`暗色主题`：跟生成结果无关，按自己的习惯设就行。",
            "The top right also holds `语言` / Language, `帮助` / Help and `暗色主题` / Dark theme. They do not affect the output — set them to taste.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "performer",
      title: l("第 1 步：选演奏者", "Step 1 — Pick the performer"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "`角色设置` 是启动后默认打开的页签。左栏 `角色参数` 卡片最上面一行是 `Avatar:` 下拉，点开就能选角色；选中的角色会显示在右栏 `人物信息` 卡片里，带一张预览图和一行 `乐器:`。",
            "`角色设置` / Character Settings is the tab the app opens on. The first row of the `角色参数` / Character Parameters card on the left is the `Avatar:` dropdown — open it and pick a character. The choice shows up in the `人物信息` / Character Info card on the right, with a preview image and an `乐器:` / Instrument line.",
          ),
        },
        {
          kind: "p",
          text: l(
            "选角色时**只看 `乐器:` 就够了**：指弹吉他、贝斯、电吉他，三者决定右手怎么拨弦。切换角色时程序会自动把对应的调弦预设套上去（贝斯变四根弦，吉他回到六弦标准调弦），所以在调弦列表里看到弦数变了不用慌。",
            "**The `乐器:` / Instrument line is all you need to check**: fingerstyle guitar, bass or electric guitar decides how the right hand plucks. Switching performers also swaps in the matching tuning preset (bass drops to four strings, guitar returns to six-string standard tuning), so a change in the string list is expected.",
          ),
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "左栏剩下的东西——`常用调弦:` 预设、`第1弦` 往下那一列音高、`降低八度`、`变调夹位置:`、`动画时间线设置:` 的三个时间、`揉弦设置:` 的两个倍率——**第一次全部保持默认**。它们分别管什么、什么时候需要动，见[软件界面详细说明](/details/fret-dance.html)。",
            "Everything else in the left column — the `常用调弦:` / Common tunings preset, the `第1弦`… / String 1… pitch rows, `降低八度` / Octave down, `变调夹位置:` / Capo position, the three timings under `动画时间线设置:` / Animation timeline settings and the two multipliers under `揉弦设置:` / Vibrato settings — **stays at its default for your first run**. What each one does and when to change it is in the [UI Reference](/details/fret-dance.html).",
          ),
        },
        {
          kind: "note",
          text: l(
            "`角色参数` 卡片里还有一个 `FPS:`（默认 60）。它决定动画的帧率，要和你场景里用的帧率一致；第一次也别动。",
            "The `角色参数` / Character Parameters card also holds `FPS:` (default 60), which sets the animation frame rate and must match the frame rate you use in your 3D scene. Leave it alone for now.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "midi",
      title: l("第 2 步：选 MIDI 文件并添加轨道", "Step 2 — Open the MIDI and add tracks"),
      image: { image: 1 },
      blocks: [
        {
          kind: "ol",
          items: [
            l(
              "切到 `MIDI选择`，在 `MIDI文件管理` 卡片里点 `📂 选择MIDI文件`，或者把文件直接拖到 `📥 将 MIDI 文件拖放到此处` 那块虚线区域。选好后文件名会显示在 `路径:` 后面。",
              "Switch to `MIDI选择` / MIDI Select and press `📂 选择MIDI文件` / Select MIDI File in the `MIDI文件管理` / MIDI File Management card, or drag the file onto the dashed `📥 将 MIDI 文件拖放到此处` / Drop a MIDI file here area. The file name then appears after `路径:` / Path.",
            ),
            l(
              "点 `🔍 扫描MIDI信息`。右栏 `轨道选择` 卡片里会出现一张表：`选择` / `轨道` / `通道` / `乐器` / `音符数`。",
              "Press `🔍 扫描MIDI信息` / Scan MIDI Info. The `轨道选择` / Track Selection card on the right fills with a table: `选择` / Select, `轨道` / Track, `通道` / Channel, `乐器` / Instrument, `音符数` / Notes.",
            ),
            l(
              "按 `乐器` 列挑出弹吉他的那几行（Guitar / Bass 之类），把左边的勾选框打上，再点 `✅ 添加选中的轨道`。选中的轨道会以徽标形式出现在左栏 `已选择的轨道:` 后面。",
              "Pick the guitar rows by the `乐器` / Instrument column (Guitar / Bass and the like), tick their checkboxes and press `✅ 添加选中的轨道` / Add selected tracks. The chosen tracks appear as badges after `已选择的轨道:` / Selected tracks on the left.",
            ),
          ],
        },
        {
          kind: "p",
          text: l(
            "左栏 `参数设置` 里的 `通道号:`、`FPS:`、`音符量化:` 第一次都保持原样：`-1` 表示不按通道过滤，`1/64` 是音符量化精度。想让节奏更贴节拍、或者反过来不想让程序挪动音符起点时，才需要改它们。",
            "Leave `通道号:` / Channel, `FPS:` and `音符量化:` / Note quantization in the `参数设置` / Parameter Settings card as they are: `-1` means no channel filtering and `1/64` is the note quantization grid. Only touch them when you want notes snapped harder onto the beat — or deliberately not moved at all.",
          ),
        },
        {
          kind: "note",
          text: l(
            "`🎵 生成优化midi文件` 是可选的：它把选中轨道里超出吉他音域的音符删掉，另存一份新 MIDI。第一次跑不用点；真要用了，记得生成后回来用 `📂 选择MIDI文件` 选中那份新文件。",
            "`🎵 生成优化midi文件` / Generate optimized MIDI file is optional: it drops notes outside the guitar's range from the selected tracks and saves a new MIDI. Skip it on your first run; if you do use it, come back and open the new file with `📂 选择MIDI文件`.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "execute",
      title: l("第 3 步：生成动画", "Step 3 — Generate"),
      image: { image: 3 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `生成动画`。左栏是本次生成的回显——`角色:`、`使用乐器:`、`FPS:`、`吉他弦音高:`、`MIDI:`、`轨道号:`、`通道号:` 全列在这里，**这些只是给你核对的，不能在这一栏直接改**；发现哪项不对，回前两个页签改。右栏是 `控制台信息`，进度和结果都刷在这里。",
            "Switch to `生成动画` / Generate. The left column echoes this run — `角色:` / Character, `使用乐器:` / Instrument, `FPS:`, `吉他弦音高:` / Guitar string pitches, `MIDI:`, `轨道号:` / Track number and `通道号:` / Channel number. **These are read-only;** fix anything wrong back on the previous two tabs. The right column is `控制台信息` / Console Output, where progress and results stream in.",
          ),
        },
        {
          kind: "p",
          text: l(
            "在左栏 `操作` 里点最下面的 `一键生成所有数据`。它会按 `1.初始化` → `2.生成左手指法` → `3.生成右手指法` → `4.生成左手动画` → `5.生成右手动画` → `6.生成弦动画` 的顺序一次跑完（如果你在 MIDI 页勾了 `启用吉他音量偏移动画`，还会跑第 7 步）。运行期间按钮下方出现 `正在执行...`。",
            "In the `操作` / Operations group on the left, press `一键生成所有数据` / Generate all data in one go. It runs `1.初始化` → `2.生成左手指法` → `3.生成右手指法` → `4.生成左手动画` → `5.生成右手动画` → `6.生成弦动画` in one pass (plus step 7 if you ticked `启用吉他音量偏移动画` / Enable guitar volume offset animation on the MIDI tab). `正在执行...` / Running... appears under the buttons while it works.",
          ),
        },
        {
          kind: "p",
          text: l(
            "想一步步看，也可以在 `操作` 里从 `1.初始化` 挨个点下来，效果和一次跑完完全一样。",
            "If you prefer watching each stage, click `1.初始化` and work down the list — the result is identical to the one-click run.",
          ),
        },
        {
          kind: "callout",
          tone: "tip",
          title: l("怎么算跑完了", "How to tell it finished"),
          text: l(
            "控制台最后一行出现 `所有数据生成完成`，并且中间没有红色的行。控制台里绿色是成功、黄色是警告、红色才是错误。",
            "The console ends with `所有数据生成完成` / All data generation complete and shows no red lines. Green means success, yellow is a warning, and only red is an error.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "result",
      title: l("生成出来的是什么，怎么用", "What you got, and how to use it"),
      image: { image: 3 },
      blocks: [
        {
          kind: "p",
          text: l(
            "程序在自己的 `output/` 目录里写出一批文件，其中**只有一份是要给插件的**：`output/final_result/{角色}_{midi文件名}_{轨道}.json`。它是本次生成的报告，里面记录着左右手动画、琴弦动画等数据文件的绝对路径。在 `控制台信息` 卡片右上角点 `复制结果`，就能把这份报告的完整路径复制到剪贴板。",
            "The app writes a batch of files into its own `output/` folder, and **only one of them goes to the plugin**: `output/final_result/{performer}_{midi}_{tracks}.json`. That is this run's report, and it records the absolute paths of the left-hand, right-hand and string animation files. Press `复制结果` / Copy Result at the top right of the `控制台信息` / Console Output card to copy the report's full path to the clipboard.",
          ),
        },
        {
          kind: "note",
          text: l(
            "报告里存的是**绝对路径**，所以生成完别把 `output/` 目录改名或搬走，否则插件找不到数据。",
            "The report stores **absolute paths**, so do not rename or move the `output/` folder afterwards — the plugin would lose track of the data.",
          ),
        },
      ],
      subs: [
        {
          id: "result-load",
          title: l("在插件里加载这份报告", "Loading the report in the plugin"),
          image: { image: 3 },
          blocks: [
            {
              kind: "ol",
              items: [
                l(
                  "在 Blender 或 Unreal 里启用免费的 MusicDoll 插件，打开 FretDance 的演奏者面板。",
                  "Enable the free MusicDoll add-on in Blender or Unreal and open its FretDance performer panel.",
                ),
                l(
                  "在面板里选中**和这次生成完全相同**的角色。",
                  "Select **the very same performer** you generated with.",
                ),
                l(
                  "加载 `output/final_result/` 里那份报告 JSON，插件会顺着里面记录的路径读齐所有数据。",
                  "Load the report JSON from `output/final_result/`; the add-on follows the paths inside it and reads all the data.",
                ),
                l(
                  "数据被写进时间线，之后就可以播放或渲染了。",
                  "The data lands on the timeline — play or render it.",
                ),
              ],
            },
            {
              kind: "p",
              text: l(
                "插件面板上每个按钮的含义，见 [Blender 插件说明](/manuals/blender/fret-dance.html) 与 [Unreal 插件说明](/manuals/unreal/fret-dance.html)。",
                "For every button on the plugin panel, see the [Blender add-on guide](/manuals/blender/fret-dance.html) and the [Unreal plugin guide](/manuals/unreal/fret-dance.html).",
              ),
            },
          ],
        },
        {
          id: "result-inspect",
          title: l("想先看一眼左手按得对不对", "Checking the left hand first"),
          image: { image: 2 },
          blocks: [
            {
              kind: "p",
              text: l(
                "不用等进 3D 软件。切到 `右手编辑器` 页签，左边默认就是 `🎸 指法时间轴` 视图：每根弦一条线，横轴是时间，线上的数字是这根弦要按的品格（`0` 就是空弦）。它按当前 MIDI 与轨道去找对应的指法文件，还没生成过就点一下 `⚙ 生成指法时间轴`。想听效果就 `▶ 播放`（走系统 MIDI 合成器），看到可疑的音符直接点那个数字，下面会显示它的时间、帧号、tick、弦、品和手指编号。",
                "No need to wait for your 3D app. Open the `右手编辑器` / Recorder Editor tab: the `🎸 指法时间轴` / Fingering timeline view is on the left by default — one line per string, time along the horizontal axis, and the number on each mark is the fret to press on that string (`0` means open string). It looks up the fingering file for the current MIDI and tracks; if none exists yet, press `⚙ 生成指法时间轴` / Generate fret timeline once. Press `▶ 播放` / Play to hear it through the system MIDI synth, and click any number to see its time, frame, tick, string, fret and finger index.",
              ),
            },
          ],
        },
        {
          id: "result-whammy",
          title: l("电吉他摇把要手工改：右手编辑", "Hand-editing electric-guitar whammy"),
          image: { image: 4 },
          blocks: [
            {
              kind: "p",
              text: l(
                "如果弹的是电吉他，动画里某几帧的摇把（whammy）抖得不对，可以在同一个页签里切到 `✏ 右手编辑`：按 `帧数:` 和 `±` 范围 `🔍 搜索` 出那几条记录，逐张卡片调 `半周期(s):` 与 `占比:`，看着 `周期数:` 变化，满意后 `💾 保存到文件`。",
                "For an electric guitar, if the whammy wobble looks wrong on a few frames, switch to `✏ 右手编辑` / Right-hand edit in the same tab: search those records with `帧数:` / Frame plus the `±` range, tune `半周期(s):` / Half period (s) and `占比:` / Usage card by card while watching `周期数:` / Cycles, then `💾 保存到文件` / Save to file.",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              title: l("改完要重跑第 5 步", "Re-run step 5 afterwards"),
              text: l(
                "这里保存的只是右手记录，动画文件不会自动更新——保存后回 `生成动画` 点一次 `5.生成右手动画`。反过来也要小心：`3.生成右手指法` 和 `一键生成所有数据` 会重新生成右手记录，**覆盖掉你的手工修改**。",
                "Saving here only updates the right-hand records; the animation file is not refreshed. Go back to `生成动画` / Generate and press `5.生成右手动画` once. Do be careful the other way round: `3.生成右手指法` and `一键生成所有数据` regenerate those records and **overwrite your edits**.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "checklist",
      title: l("跑通前的自检清单", "Checklist before you call it done"),
      blocks: [
        {
          kind: "table",
          head: [l("检查项", "Check"), l("应该看到什么", "What you should see")],
          rows: [
            [
              l("MIDI 选对了", "Right MIDI file"),
              l(
                "`MIDI文件管理` 卡片 `路径:` 后面是你自己的文件名，不是预填的示例名。",
                "The `MIDI文件管理` / MIDI File Management card shows your own file name after `路径:` / Path, not the pre-filled sample.",
              ),
            ],
            [
              l("轨道加进去了", "Tracks added"),
              l(
                "`已选择的轨道:` 后面有对应的 `轨道 {}` 徽标，而不是残留的默认 `轨道 1`。",
                "`已选择的轨道:` / Selected tracks lists the matching `轨道 {}` / `Track {}` badges instead of the leftover default `轨道 1` / Track 1.",
              ),
            ],
            [
              l("控制台没有红字", "No red lines"),
              l("整段输出里没有红色的错误行；黄色只是警告。", "No red error lines in the output; yellow is only a warning."),
            ],
            [
              l("跑到底了", "It ran to the end"),
              l("最后一行是 `所有数据生成完成`。", "The last line reads `所有数据生成完成` / All data generation complete."),
            ],
            [
              l("报告文件在", "Report exists"),
              l(
                "`复制结果` 拿到的那个 `output/final_result/*.json` 确实存在，且生成完之后没有被移动。",
                "The `output/final_result/*.json` you got from `复制结果` / Copy Result really exists and has not been moved since.",
              ),
            ],
            [
              l("插件能读", "The plugin can read it"),
              l(
                "插件里选中同一个角色后能加载这份 JSON，时间线上出现左右手与琴弦动画。",
                "With the same performer selected, the plugin loads the JSON and left-hand, right-hand and string animation appear on the timeline.",
              ),
            ],
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "faq",
      title: l("常见问题", "FAQ"),
      blocks: [
        {
          kind: "callout",
          tone: "info",
          title: l("还没选文件，扫描就报错？", "Scanning fails before I pick anything"),
          text: l(
            "程序启动时预填了一个示例 MIDI 路径。你的安装目录里没有这个文件就会报错，点 `📂 选择MIDI文件` 换成自己的文件再扫描。",
            "The app pre-fills a sample MIDI path on startup. If that file is not in your install folder the scan fails — press `📂 选择MIDI文件` and choose your own file, then scan again.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("轨道表格是空的？", "The track table is empty"),
          text: l(
            "多半是还没点 `🔍 扫描MIDI信息`；点过还是空的，说明这份 MIDI 里没有音符。",
            "Most likely you have not pressed `🔍 扫描MIDI信息` yet. If it is still empty after scanning, the file contains no notes.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("吉他有动画，但手不动、或该响的弦没响？", "The guitar animated, but hands barely move or strings stay silent"),
          text: l(
            "先回 `MIDI选择` 核对 `已选择的轨道:` 是不是弹琴那一轨，再回 `角色设置` 看调弦与 `降低八度`、`变调夹位置:` 是否和曲子的记谱一致。这类问题基本都出在这两处，具体参数含义见[软件界面详细说明](/details/fret-dance.html)。",
            "Go back to `MIDI选择` / MIDI Select and check that `已选择的轨道:` / Selected tracks holds the guitar track, then to `角色设置` / Character Settings and check the tuning, `降低八度` / Octave down and `变调夹位置:` / Capo position against how the song is notated. Problems of this kind almost always come from those two places — see the [UI Reference](/details/fret-dance.html) for what each parameter means.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("换一首曲子要重做什么？", "What do I redo for a new song?"),
          text: l(
            "回 `MIDI选择` 选新文件 → `🔍 扫描MIDI信息` → 勾轨道 → `✅ 添加选中的轨道`，再回 `生成动画` 点 `一键生成所有数据`。演奏者不用重选。",
            "Go to `MIDI选择` / MIDI Select, open the new file, `🔍 扫描MIDI信息` / Scan MIDI Info, tick the tracks, `✅ 添加选中的轨道` / Add selected tracks, then back to `生成动画` / Generate and press `一键生成所有数据` / Generate all data in one go. The performer stays as it is.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l("报告路径里有中文和空格，插件能读吗？", "The report path contains spaces and non-ASCII — will the plugin read it?"),
          text: l(
            "能。报告里存的是绝对路径，插件按路径读文件；要小心的是生成之后别再移动 `output/` 目录。",
            "Yes. The report holds absolute paths and the plugin reads the files by path. The thing to avoid is moving the `output/` folder after generating.",
          ),
        },
      ],
    },
  ],
};
